
    import { defineComponent, ref, onMounted, nextTick, watch } from 'vue'
    import { Plan, RevenueScaleColumn, KpiScaleColumn, Salary } from '../../types/plan.types'
    import ColumnControls from '../common/ColumnControls.vue'
    import ActivationToggle from '../common/ActivationToggle.vue'
    import InfoIcon from '../common/InfoIcon.vue'
    import { planService } from '../../services/planService'
    import Chart from 'chart.js'

    interface Task {
        id: string
        name: string
        description?: string
        significance: number
        norms: Record<string, Record<string, number>>
        limits: Record<string, Record<string, number>>
    }

    interface RevenuePlan {
        branchA: number
        branchB: number
    }

    export default defineComponent({
        name: 'PlanSettings',
        components: {
            ColumnControls,
            ActivationToggle,
            InfoIcon,
        },
        props: {
            plan: {
                type: Object as () => Plan,
                required: true,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            availableTasks: {
                type: Array,
                default: () => [],
            },
        },
        emits: ['save', 'publish', 'copy', 'delete', 'cancel', 'open-plan-settings-modal', 'activate-plan'],
        data() {
            return {
                revenueScale: [] as RevenueScaleColumn[],
                branchRevenueScales: {} as Record<string, RevenueScaleColumn[]>,
                kpiScale: [] as KpiScaleColumn[],
                tasks: [] as Task[],
                revenuePlan: {
                    branchA: 0,
                    branchB: 0,
                } as RevenuePlan,
                salary: {
                    fixedPart: 0,
                    kpiBonus: 0,
                } as Salary,
                selectedColumn: -1 as number,
                selectedBranchColumns: {} as Record<string, number>,
                dailyRevenuePlan: {} as Record<string, number>,
                generalDailyRevenuePlan: 0 as number,
                _onDocumentClickOutside: null as null | ((event: MouseEvent) => void),

                kpiScaleColumns: [] as KpiScaleColumn[],
                selectedKpiColumn: -1 as number,

                // Chart instances
                revenueChartInstances: {} as Record<string, Chart | null>,
                revenueChartGeneralInstance: null as Chart | null,
                kpiChartInstance: null as Chart | null,
                isUpdatingCharts: false as boolean,

                // Spoiler groups state
                normsGroupExpanded: true as boolean,
                bonusesGroupExpanded: false as boolean,
                isPageRebuilding: false as boolean,
                publishValidationRequested: false as boolean,
            }
        },
        computed: {
            dynamicTasks() {
                return (this as any).tasks
            },

            isPublishedPlan() {
                return (this as any).plan.status === 'published'
            },

            canPublishPlan() {
                return (this as any).isNormsGroupValid && (this as any).isBonusesGroupValid
            },

            // Валидация группы "Нормы плана"
            isNormsGroupValid() {
                const component = this as any

                // Проверка норм по задачам - все нормы должны быть > 0
                const tasks = component.dynamicTasks || []
                if (tasks.length === 0) {
                    return false
                }

                // Проверка плана по дневной выручке
                if (component.plan.branchIds && component.plan.branchIds.length > 0) {
                    const isRevenuePlanValid = component.plan.branchIds.every((branchId: string) => {
                        const value = component.dailyRevenuePlan?.[branchId]
                        return value !== undefined && value !== null && Number(value) > 0
                    })
                    if (!isRevenuePlanValid) {
                        return false
                    }
                } else {
                    if (
                        component.generalDailyRevenuePlan === undefined ||
                        component.generalDailyRevenuePlan === null ||
                        Number(component.generalDailyRevenuePlan) <= 0
                    ) {
                        return false
                    }
                }

                const branchIds = component.plan.branchIds && component.plan.branchIds.length > 0
                    ? component.plan.branchIds
                    : ['general']

                for (const task of tasks) {
                    for (const branchId of branchIds) {
                        const normValue = task.norms && task.norms[branchId] && task.norms[branchId]['general']
                        if (!normValue || normValue === 0) {
                            return false
                        }
                    }
                }

                // Проверка размера дневного оклада и номинала бонуса
                if (!component.salary || !component.salary.fixedPart || component.salary.fixedPart <= 0) {
                    return false
                }

                if (!component.salary || !component.salary.kpiBonus || component.salary.kpiBonus <= 0) {
                    return false
                }

                return true
            },

            // Валидация группы "Шкала премий"
            isBonusesGroupValid() {
                const component = this as any

                // Проверка ограничений по максимальному размеру выполнения
                const tasks = component.dynamicTasks || []
                if (tasks.length === 0) {
                    return false
                }

                const branchIds = component.plan.branchIds && component.plan.branchIds.length > 0
                    ? component.plan.branchIds
                    : ['general']

                for (const task of tasks) {
                    for (const branchId of branchIds) {
                        const limitValue = task.limits && task.limits[branchId] && task.limits[branchId]['general']
                        if (!limitValue || limitValue === 0) {
                            return false
                        }
                    }
                }

                return true
            },
        },
        watch: {
            // Watchers отключены для предотвращения бесконечного цикла
            // Графики обновляются при mounted и при явных действиях пользователя
            bonusesGroupExpanded(newVal: boolean) {
                // Когда группа "Шкала премий" разворачивается, создаем графики
                if (newVal) {
                    this.$nextTick(() => {
                        // Создаем графики для всех филиалов
                        if (this.plan.branchIds && this.plan.branchIds.length > 1) {
                            this.plan.branchIds.forEach((branchId: string) => {
                                this.createRevenueChart(branchId)
                            })
                        }
                        this.createRevenueChartGeneral()
                        this.createKpiChart()
                    })
                }
            },
            'plan.branchIds': {
                async handler(newVal: string[] = [], oldVal: string[] = []) {
                    // При изменении количества филиалов сворачиваем "Шкалу премий",
                    // чтобы не рендерить графики в промежуточном состоянии.
                    if (!oldVal || !Array.isArray(oldVal)) return
                    const oldLength = oldVal.length
                    const newLength = Array.isArray(newVal) ? newVal.length : 0
                    if (oldLength !== newLength) {
                        await this.blockPageDuringRebuild(() => {
                            this.bonusesGroupExpanded = false
                            this.loadTasks()
                        })
                    }
                },
                deep: true,
            },
        },
        mounted() {
            this.loadTasks()
            this.setupClickOutside()
            // Создаем графики только если группа "Шкала премий" развернута
            // Иначе графики создадутся при разворачивании группы через watcher
            if (this.bonusesGroupExpanded) {
                this.$nextTick(() => {
                    // Создаем графики для всех филиалов
                    if (this.plan.branchIds && this.plan.branchIds.length > 1) {
                        this.plan.branchIds.forEach((branchId: string) => {
                            this.createRevenueChart(branchId)
                        })
                    }
                    this.createRevenueChartGeneral()
                    this.createKpiChart()
                })
            }
        },
        beforeDestroy() {
            if (this._onDocumentClickOutside) {
                document.removeEventListener('click', this._onDocumentClickOutside)
                this._onDocumentClickOutside = null
            }
        },
        methods: {
            getDefaultRevenueScale(): RevenueScaleColumn[] {
                return [{ revenue: 0, bonus: 0, percentage: 0 }]
            },
            blockPageDuringRebuild(rebuildFn?: () => void): Promise<void> {
                this.isPageRebuilding = true
                return new Promise(resolve => {
                    try {
                        if (rebuildFn) {
                            rebuildFn()
                        }
                    } finally {
                        // Ждём завершения рендера и пересоздания данных/DOM,
                        // затем снимаем блокировку страницы.
                        this.$nextTick(() => {
                            requestAnimationFrame(() => {
                                requestAnimationFrame(() => {
                                    this.isPageRebuilding = false
                                    resolve()
                                })
                            })
                        })
                    }
                })
            },
            openPlanSettingsModal() {
                this.$emit('open-plan-settings-modal', this.plan)
            },

            togglePlanActivation(isActive: boolean) {
                // Переключаем состояние активации плана
                this.$set(this.plan, 'isActive', isActive)

                // Если план активируется, эмитим событие для родительского компонента
                this.$emit('activate-plan', this.plan)
            },

            toggleGroup(groupName: string) {
                if (groupName === 'norms') {
                    this.normsGroupExpanded = !this.normsGroupExpanded
                } else if (groupName === 'bonuses') {
                    this.bonusesGroupExpanded = !this.bonusesGroupExpanded
                }
            },

            loadTasks() {
                const availableTasks = (this as any).availableTasks || []
                
                if (this.plan.tasks && this.plan.tasks.length > 0) {
                    this.tasks = this.plan.tasks.map((planTask: any) => {
                        // Ищем описание в availableTasks, если его нет в planTask
                        let description = planTask.description || ''
                        if (!description) {
                            const availableTask = availableTasks.find((t: any) => t.id === planTask.id)
                            if (availableTask && availableTask.description) {
                                description = availableTask.description
                            }
                        }
                        
                        return {
                            id: planTask.id,
                            name: planTask.name,
                            description: description,
                            significance: planTask.significance || 0,
                            norms: planTask.norms || {},
                            limits: planTask.limits || {},
                        }
                    })
                } else if ((this.plan as any).taskIds && (this.plan as any).taskIds.length > 0) {
                    // Если есть taskIds, но нет tasks, создаем задачи с данными из availableTasks
                    const planTaskIds = (this.plan as any).taskIds
                    this.tasks = planTaskIds.map((taskId: string) => {
                        const availableTask = availableTasks.find((t: any) => t.id === taskId)
                        return {
                            id: taskId,
                            name: availableTask ? availableTask.name : taskId,
                            description: availableTask ? (availableTask.description || '') : '',
                            significance: 0,
                            norms: {},
                            limits: {},
                        }
                    })
                }

                // Загружаем данные о шкалах и зарплате из плана
                this.loadPlanData()
            },

            loadPlanData() {
                // Загружаем шкалу выручки
                if (this.plan.revenueScale) {
                    if (Array.isArray(this.plan.revenueScale)) {
                        // Если это массив, то это данные для одного филиала
                        const singleScale = this.plan.revenueScale as RevenueScaleColumn[]
                        this.revenueScale =
                            singleScale && singleScale.length > 0
                                ? singleScale.map((item: RevenueScaleColumn) => ({ ...item }))
                                : this.getDefaultRevenueScale()
                        this.branchRevenueScales = {}
                    } else {
                        // Если это объект с филиалами
                        this.branchRevenueScales = this.plan.revenueScale as Record<string, RevenueScaleColumn[]>
                        const branchIds = this.plan.branchIds || []

                        // При одном (или нуле) филиале показываем "общую" таблицу,
                        // поэтому переносим данные из branch-структуры в revenueScale.
                        if (branchIds.length <= 1) {
                            const preferredBranch = branchIds[0]
                            const preferredScale = preferredBranch
                                ? this.branchRevenueScales[preferredBranch]
                                : undefined
                            const fallbackScale = Object.values(this.branchRevenueScales || {})[0] || []
                            const sourceScale = preferredScale || fallbackScale

                            this.revenueScale =
                                sourceScale && sourceScale.length > 0
                                    ? sourceScale.map((item: RevenueScaleColumn) => ({ ...item }))
                                    : this.getDefaultRevenueScale()
                        } else {
                            // Для режима нескольких филиалов общая таблица не используется.
                            this.revenueScale = []
                        }
                    }
                } else {
                    // Если шкала не задана, не оставляем таблицу пустой в режиме одного филиала.
                    const branchIds = this.plan.branchIds || []
                    this.revenueScale = branchIds.length <= 1 ? this.getDefaultRevenueScale() : []
                    this.branchRevenueScales = {}
                }

                // Загружаем KPI шкалу
                if ((this.plan as any).kpiScale) {
                    this.kpiScaleColumns = (this.plan as any).kpiScale as KpiScaleColumn[]
                }

                // Загружаем зарплату
                if (this.plan.salary) {
                    this.salary = this.plan.salary as Salary
                }

                // Загружаем план выручки
                if (this.plan.dailyRevenuePlan) {
                    if (this.plan.branchIds && this.plan.branchIds.length > 0) {
                        this.dailyRevenuePlan = this.plan.dailyRevenuePlan
                    } else {
                        this.generalDailyRevenuePlan = this.plan.dailyRevenuePlan.general || 0
                    }
                }
            },

            addRevenueColumnLeft(index: number) {
                this.revenueScale.splice(index, 0, {
                    revenue: 0,
                    bonus: 0,
                    percentage: 0,
                })
                this.selectedColumn = index

                // Обновляем только общий график
                this.$nextTick(() => {
                    this.createRevenueChartGeneral()
                })
            },

            addRevenueColumnRight(index: number) {
                this.revenueScale.splice(index + 1, 0, {
                    revenue: 0,
                    bonus: 0,
                    percentage: 0,
                })
                this.selectedColumn = index + 1

                // Обновляем только общий график
                this.$nextTick(() => {
                    this.createRevenueChartGeneral()
                })
            },

            removeRevenueColumn(index: number) {
                if (this.revenueScale.length <= 1) {
                    return
                }

                const column = this.revenueScale[index]
                const hasValues = column.revenue > 0 || column.bonus > 0 || column.percentage > 0

                if (hasValues) {
                    if (!confirm('В этом столбце есть данные. Вы уверены, что хотите его удалить?')) {
                        return
                    }
                }

                this.revenueScale.splice(index, 1)

                // Обновляем selectedColumn если нужно
                if (this.selectedColumn >= index) {
                    this.selectedColumn = Math.max(0, this.selectedColumn - 1)
                }

                // Обновляем только общий график
                this.$nextTick(() => {
                    this.createRevenueChartGeneral()
                })
            },

            // Методы для работы с отдельными шкалами филиалов
            getBranchRevenueScale(branchId: string): RevenueScaleColumn[] {
                return this.branchRevenueScales[branchId] || []
            },

            getSelectedColumn(branchId: string): number {
                return this.selectedBranchColumns[branchId]
            },

            setSelectedColumn(branchId: string, index: number) {
                this.$set(this.selectedBranchColumns, branchId, index)
            },

            addBranchRevenueColumnLeft(branchId: string, index: number) {
                const scale = this.getBranchRevenueScale(branchId)
                scale.splice(index, 0, {
                    revenue: 0,
                    bonus: 0,
                    percentage: 0,
                })
                this.setSelectedColumn(branchId, index)

                // Обновляем только график для этого филиала
                this.$nextTick(() => {
                    this.createRevenueChart(branchId)
                })
            },

            addBranchRevenueColumnRight(branchId: string, index: number) {
                const scale = this.getBranchRevenueScale(branchId)
                scale.splice(index + 1, 0, {
                    revenue: 0,
                    bonus: 0,
                    percentage: 0,
                })
                this.setSelectedColumn(branchId, index + 1)

                // Обновляем только график для этого филиала
                this.$nextTick(() => {
                    this.createRevenueChart(branchId)
                })
            },

            removeBranchRevenueColumn(branchId: string, index: number) {
                const scale = this.getBranchRevenueScale(branchId)
                if (scale.length <= 1) {
                    return
                }

                const column = scale[index]
                const hasValues = column.revenue > 0 || column.bonus > 0 || column.percentage > 0

                if (hasValues) {
                    if (!confirm('В этом столбце есть данные. Вы уверены, что хотите его удалить?')) {
                        return
                    }
                }

                scale.splice(index, 1)

                // Обновляем selectedColumn если нужно
                const currentSelected = this.getSelectedColumn(branchId)
                if (currentSelected >= index) {
                    this.setSelectedColumn(branchId, Math.max(0, currentSelected - 1))
                }

                // Обновляем только график для этого филиала
                this.$nextTick(() => {
                    this.createRevenueChart(branchId)
                })
            },

            // KPI Scale methods
            selectKpiColumn(index: number) {
                this.selectedKpiColumn = index
            },

            addKpiColumnLeft(index: number) {
                // Добавляем новый столбец в массив columns
                const newColumn = {
                    id: Date.now(),
                    completion: 100.0,
                    bonusPercentage: 100.0,
                }
                this.kpiScaleColumns.splice(index, 0, newColumn)
                this.selectedKpiColumn = index

                // Обновляем только KPI график
                this.$nextTick(() => {
                    this.createKpiChart()
                })
            },

            addKpiColumnRight(index: number) {
                // Добавляем новый столбец в массив columns
                const newColumn = {
                    id: Date.now(),
                    completion: 100.0,
                    bonusPercentage: 100.0,
                }
                this.kpiScaleColumns.splice(index + 1, 0, newColumn)
                this.selectedKpiColumn = index + 1

                // Обновляем только KPI график
                this.$nextTick(() => {
                    this.createKpiChart()
                })
            },

            removeKpiColumn(index: number) {
                if (this.kpiScaleColumns.length <= 1) {
                    return
                }

                // Проверяем, есть ли данные в столбце
                const column = this.kpiScaleColumns[index]
                const hasData = column.completion !== 100.0 || column.bonusPercentage !== 100.0

                if (hasData) {
                    if (confirm('В столбце есть данные. Удалить столбец?')) {
                        this.kpiScaleColumns.splice(index, 1)
                        this.selectedKpiColumn = -1

                        // Обновляем только KPI график
                        this.$nextTick(() => {
                            this.createKpiChart()
                        })
                    }
                } else {
                    this.kpiScaleColumns.splice(index, 1)
                    this.selectedKpiColumn = -1

                    // Обновляем только KPI график
                    this.$nextTick(() => {
                        this.createKpiChart()
                    })
                }
            },

            // Методы для обновления KPI значений
            updateKpiCompletion(index: number, event: any) {
                const value = parseFloat((event.target as HTMLInputElement).value) || 0
                this.$set(this.kpiScaleColumns[index], 'completion', value)

                // Обновляем только KPI график
                this.$nextTick(() => {
                    this.createKpiChart()
                })
            },

            updateKpiBonusPercentage(index: number, event: any) {
                const value = parseFloat((event.target as HTMLInputElement).value) || 0
                this.$set(this.kpiScaleColumns[index], 'bonusPercentage', value)

                // Обновляем только KPI график
                this.$nextTick(() => {
                    this.createKpiChart()
                })
            },

            formatDate(date: string | Date): string {
                const d = new Date(date)
                return d.toLocaleDateString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                })
            },

            getStatusText(status: string): string {
                const statusMap: Record<string, string> = {
                    draft: 'Черновик',
                    published: 'Опубликован',
                    archived: 'Архив',
                }
                return statusMap[status] || status
            },

            publishPlan() {
                this.publishValidationRequested = true

                if (!this.canPublishPlan) {
                    // Подсветка полей и раскрытие секций с ошибками
                    this.normsGroupExpanded = true
                    this.bonusesGroupExpanded = true
                    return
                }
                this.$emit('publish', this.getFormData())
            },

            shouldHighlightNorm(task: Task, branchId: string): boolean {
                if (!this.publishValidationRequested) return false
                const normValue = task.norms && task.norms[branchId] && task.norms[branchId]['general']
                return !normValue || normValue <= 0
            },

            shouldHighlightLimit(task: Task, branchId: string): boolean {
                if (!this.publishValidationRequested) return false
                const limitValue = task.limits && task.limits[branchId] && task.limits[branchId]['general']
                return !limitValue || limitValue <= 0
            },

            shouldHighlightSalary(field: 'fixedPart' | 'kpiBonus'): boolean {
                if (!this.publishValidationRequested) return false
                const value = this.salary ? this.salary[field] : 0
                return !value || value <= 0
            },

            shouldHighlightDailyRevenuePlan(branchId: string): boolean {
                if (!this.publishValidationRequested) return false
                const value = this.dailyRevenuePlan?.[branchId]
                return value === undefined || value === null || Number(value) <= 0
            },

            shouldHighlightGeneralDailyRevenuePlan(): boolean {
                if (!this.publishValidationRequested) return false
                return (
                    this.generalDailyRevenuePlan === undefined ||
                    this.generalDailyRevenuePlan === null ||
                    Number(this.generalDailyRevenuePlan) <= 0
                )
            },

            saveDraft() {
                this.$emit('save', this.getFormData())
            },

            createCopy() {
                this.$emit('copy', this.plan)
            },

            deletePlan() {
                if (confirm('Вы уверены, что хотите удалить этот план?')) {
                    this.$emit('delete', this.plan)
                }
            },

            cancel() {
                this.$emit('cancel')
            },

            getFormData() {
                return {
                    // Шкала ежедневной премии
                    revenueScale:
                        this.plan.branchIds && this.plan.branchIds.length > 1
                            ? this.branchRevenueScales
                            : this.revenueScale,

                    // Шкала KPI (новая структура)
                    kpiScale: this.kpiScaleColumns,

                    // Задачи (динамические)
                    tasks: this.dynamicTasks,

                    // План по дневной выручке
                    dailyRevenuePlan:
                        this.plan.branchIds && this.plan.branchIds.length > 0
                            ? this.dailyRevenuePlan
                            : { general: this.generalDailyRevenuePlan },
                    generalDailyRevenuePlan: this.generalDailyRevenuePlan,

                    // Зарплата
                    salary: this.salary,

                    // Статус
                    status: this.plan.status,
                }
            },

            setupClickOutside() {
                if (this._onDocumentClickOutside) return

                this._onDocumentClickOutside = (event: MouseEvent) => {
                    const target = event.target as HTMLElement

                    // Проверяем клики вне всех таблиц и их элементов управления
                    const isInsideRevenueTable = target.closest('.revenue-scale-table')
                    const isInsideKpiTable = target.closest('.kpi-scale-table')
                    const isInsideTasksTable = target.closest('.tasks-table')
                    const isInsideLimitsTable = target.closest('.limits-table')
                    const isInsideColumnControls = target.closest('.column-controls')
                    const isInsideTableInput = target.closest('input[type="number"]')

                    // Если клик не внутри таблиц или элементов управления
                    if (
                        !isInsideRevenueTable &&
                        !isInsideKpiTable &&
                        !isInsideTasksTable &&
                        !isInsideLimitsTable &&
                        !isInsideColumnControls &&
                        !isInsideTableInput
                    ) {
                        this.clearAllSelections()
                    }
                }

                document.addEventListener('click', this._onDocumentClickOutside)
            },

            handlePageClick(event: Event) {
                // Обработчик клика по странице - сбрасываем выделения
                const target = event.target as HTMLElement

                // Не сбрасываем если клик по элементам управления таблиц
                if (
                    target.closest('.column-controls') ||
                    target.closest('input[type="number"]') ||
                    target.closest('th') ||
                    target.closest('td')
                ) {
                    return
                }

                this.clearAllSelections()
            },

            handleSectionClick(event: Event) {
                // Обработчик клика по заголовку секции - сбрасываем выделения
                event.stopPropagation()
                this.clearAllSelections()
            },

            clearAllSelections() {
                // Сбрасываем выделение для всех типов столбцов
                this.selectedColumn = -1
                this.selectedKpiColumn = -1

                // Сбрасываем выделение для филиалов
                if (this.plan.branchIds && this.plan.branchIds.length > 1) {
                    this.plan.branchIds.forEach((branchId: string) => {
                        this.$set(this.selectedBranchColumns, branchId, -1)
                    })
                }
            },

            createDynamicNorms(): Record<string, Record<string, number>> {
                const norms: Record<string, Record<string, number>> = {}

                const branches =
                    this.plan.branchIds && this.plan.branchIds.length > 0 ? this.plan.branchIds : ['general']
                const channels = ['general']

                branches.forEach(branchId => {
                    norms[branchId] = {}
                    channels.forEach(channelId => {
                        norms[branchId][channelId] = 0
                    })
                })

                return norms
            },

            createDynamicLimits(): Record<string, Record<string, number>> {
                const limits: Record<string, Record<string, number>> = {}

                const branches =
                    this.plan.branchIds && this.plan.branchIds.length > 0 ? this.plan.branchIds : ['general']
                const channels = ['general']

                branches.forEach(branchId => {
                    limits[branchId] = {}
                    channels.forEach(channelId => {
                        limits[branchId][channelId] = 150
                    })
                })

                return limits
            },

            // Методы для безопасного обновления значений norms и limits
            updateNormValue(task: Task, branchId: string, channelId: string, event: any) {
                const value = (event.target as HTMLInputElement).value
                const numValue = parseFloat(value) || 0
                if (!task.norms[branchId]) {
                    this.$set(task.norms, branchId, {})
                }
                this.$set(task.norms[branchId], channelId, numValue)
            },

            updateLimitValue(task: Task, branchId: string, channelId: string, event: any) {
                const value = (event.target as HTMLInputElement).value
                const numValue = parseFloat(value) || 0
                if (!task.limits[branchId]) {
                    this.$set(task.limits, branchId, {})
                }
                this.$set(task.limits[branchId], channelId, numValue)
            },

            // Метод для обновления значимости задачи с валидацией и автосуммой
            updateTaskSignificance(task: Task, event: any) {
                const value = (event.target as HTMLInputElement).value
                let numValue = parseFloat(value) || 0

                // Ограничиваем значение от 0 до 100
                numValue = Math.max(0, Math.min(100, numValue))

                // Обновляем значение задачи
                this.$set(task, 'significance', numValue)

                // Автоматическая корректировка для поддержания суммы = 100%
                this.adjustTaskSignificanceSum(task)

                // Обновляем значение в input, если оно изменилось
                const inputElement = event.target as HTMLInputElement
                if (inputElement.value !== numValue.toString()) {
                    inputElement.value = numValue.toString()
                }
            },

            // Метод для корректировки суммы значимости задач
            adjustTaskSignificanceSum(changedTask: Task) {
                if (!this.dynamicTasks || this.dynamicTasks.length === 0) {
                    return
                }

                const tasks = this.dynamicTasks
                const changedTaskIndex = tasks.findIndex(t => t.id === changedTask.id)

                if (changedTaskIndex === -1) {
                    return
                }

                // Вычисляем текущую сумму всех задач
                const currentSum = tasks.reduce((sum, t) => sum + (t.significance || 0), 0)

                // Вычисляем разность для корректировки
                const difference = 100 - currentSum

                // Определяем порядок корректировки задач
                let adjustOrder: number[]
                if (changedTaskIndex === tasks.length - 1) {
                    // Если изменена последняя задача, корректируем начиная с первой
                    adjustOrder = Array.from({ length: tasks.length - 1 }, (_, i) => i)
                } else {
                    // Иначе корректируем начиная с последней
                    adjustOrder = Array.from({ length: tasks.length - 1 }, (_, i) => tasks.length - 1 - i)
                }

                // Последовательно корректируем задачи
                let remainingDifference = difference

                for (const index of adjustOrder) {
                    if (Math.abs(remainingDifference) === 0) {
                        break // Если разность уже скорректирована
                    }

                    const currentValue = tasks[index].significance || 0
                    const newValue = currentValue + remainingDifference

                    // Ограничиваем новое значение от 0 до 100
                    const adjustedValue = Math.max(0, Math.min(100, Math.round(newValue * 10) / 10))
                    const actualAdjustment = adjustedValue - currentValue

                    // Обновляем значение задачи
                    this.$set(tasks[index], 'significance', adjustedValue)

                    // Вычисляем оставшуюся разность
                    remainingDifference -= actualAdjustment
                }
            },

            // Методы для автоматического расчета процента в шкале выручки
            updateRevenueValue(branchId: string, index: number, event: any) {
                const value = (event.target as HTMLInputElement).value
                const numValue = parseFloat(value) || 0
                const scale = this.getBranchRevenueScale(branchId)
                this.$set(scale[index], 'revenue', numValue)

                // Пересчитываем процент на основе выручки и премии
                if (numValue > 0) {
                    const newPercentage = (scale[index].bonus / numValue) * 100
                    this.$set(scale[index], 'percentage', Math.round(newPercentage * 100) / 100)
                }

                // Обновляем только график для этого филиала
                this.$nextTick(() => {
                    this.createRevenueChart(branchId)
                })
            },

            updateBonusValue(branchId: string, index: number, event: any) {
                const value = (event.target as HTMLInputElement).value
                const numValue = parseFloat(value) || 0
                const scale = this.getBranchRevenueScale(branchId)
                this.$set(scale[index], 'bonus', numValue)

                // Пересчитываем процент на основе выручки и премии
                if (scale[index].revenue > 0) {
                    const newPercentage = (numValue / scale[index].revenue) * 100
                    this.$set(scale[index], 'percentage', Math.round(newPercentage * 100) / 100)
                }

                // Обновляем только график для этого филиала
                this.$nextTick(() => {
                    this.createRevenueChart(branchId)
                })
            },

            updatePercentageValue(branchId: string, index: number, event: any) {
                const value = (event.target as HTMLInputElement).value
                const numValue = parseFloat(value) || 0
                const scale = this.getBranchRevenueScale(branchId)
                this.$set(scale[index], 'percentage', numValue)

                // Пересчитываем премию на основе процента и выручки
                if (scale[index].revenue > 0) {
                    const newBonus = (numValue / 100) * scale[index].revenue
                    this.$set(scale[index], 'bonus', Math.round(newBonus))
                }

                // Обновляем только график для этого филиала
                this.$nextTick(() => {
                    this.createRevenueChart(branchId)
                })
            },

            // Методы для автоматического расчета в основной таблице
            updateMainRevenueValue(index: number, event: any) {
                const value = (event.target as HTMLInputElement).value
                const numValue = parseFloat(value) || 0
                this.$set(this.revenueScale[index], 'revenue', numValue)

                // Пересчитываем процент на основе выручки и премии
                if (numValue > 0) {
                    const newPercentage = (this.revenueScale[index].bonus / numValue) * 100
                    this.$set(this.revenueScale[index], 'percentage', Math.round(newPercentage * 100) / 100)
                }

                // Обновляем только общий график
                this.$nextTick(() => {
                    this.createRevenueChartGeneral()
                })
            },

            updateMainBonusValue(index: number, event: any) {
                const value = (event.target as HTMLInputElement).value
                const numValue = parseFloat(value) || 0
                this.$set(this.revenueScale[index], 'bonus', numValue)

                // Пересчитываем процент на основе выручки и премии
                if (this.revenueScale[index].revenue > 0) {
                    const newPercentage = (numValue / this.revenueScale[index].revenue) * 100
                    this.$set(this.revenueScale[index], 'percentage', Math.round(newPercentage * 100) / 100)
                }

                // Обновляем только общий график
                this.$nextTick(() => {
                    this.createRevenueChartGeneral()
                })
            },

            updateMainPercentageValue(index: number, event: any) {
                const value = (event.target as HTMLInputElement).value
                const numValue = parseFloat(value) || 0
                this.$set(this.revenueScale[index], 'percentage', numValue)

                // Пересчитываем премию на основе процента и выручки
                if (this.revenueScale[index].revenue > 0) {
                    const newBonus = (numValue / 100) * this.revenueScale[index].revenue
                    this.$set(this.revenueScale[index], 'bonus', Math.round(newBonus))
                }

                // Обновляем только общий график
                this.$nextTick(() => {
                    this.createRevenueChartGeneral()
                })
            },

            // Методы для графиков
            createRevenueChart(branchId: string) {
                const canvas = this.$refs[`revenueChart_${branchId}`] as HTMLCanvasElement
                if (!canvas) return

                // Уничтожаем предыдущий график если есть
                if (this.revenueChartInstances[branchId]) {
                    this.revenueChartInstances[branchId]!.destroy()
                }

                // Получаем данные из таблицы выручки для конкретного филиала
                const revenueData = this.getRevenueScaleDataForBranch(branchId)

                this.revenueChartInstances[branchId] = new Chart(canvas, {
                    type: 'bar',
                    data: {
                        labels: revenueData.labels,
                        datasets: revenueData.datasets,
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        title: {
                            display: false,
                        },
                        legend: {
                            display: true,
                            position: 'top',
                        },
                        tooltips: {
                            mode: 'index',
                            intersect: false,
                            displayColors: true,
                            callbacks: {
                                title: function() {
                                    return ''
                                },
                                label: function(context: any) {
                                    const label = context.dataset?.label || ''
                                    const value = context.parsed?.y ?? context.value ?? 0
                                    if (label.includes('Процент')) {
                                        return `${label}: ${value}%`
                                    }
                                    return `${label}: ${value.toLocaleString('ru-RU')}`
                                },
                            },
                        },
                        scales: {
                            xAxes: [
                                {
                                    scaleLabel: {
                                        display: true,
                                        labelString: 'Выручка (Р)',
                                    },
                                    stacked: false,
                                },
                            ],
                            yAxes: [
                                {
                                    scaleLabel: {
                                        display: true,
                                        labelString: 'Премия (Р) / Процент (%)',
                                    },
                                    ticks: {
                                        beginAtZero: true,
                                        callback: function(value: any) {
                                            return value.toLocaleString('ru-RU')
                                        },
                                    },
                                    stacked: false,
                                },
                            ],
                        },
                    },
                })
            },

            createRevenueChartGeneral() {
                const canvas = this.$refs.revenueChartGeneral as HTMLCanvasElement
                if (!canvas) return

                // Уничтожаем предыдущий график если есть
                if (this.revenueChartGeneralInstance) {
                    this.revenueChartGeneralInstance.destroy()
                }

                // Получаем данные из общей таблицы выручки
                const revenueData = this.getRevenueScaleData()

                this.revenueChartGeneralInstance = new Chart(canvas, {
                    type: 'bar',
                    data: {
                        labels: revenueData.labels,
                        datasets: revenueData.datasets,
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        title: {
                            display: false,
                        },
                        legend: {
                            display: true,
                            position: 'top',
                        },
                        tooltips: {
                            mode: 'index',
                            intersect: false,
                            displayColors: true,
                            callbacks: {
                                title: function() {
                                    return ''
                                },
                                label: function(context: any) {
                                    const label = context.dataset?.label || ''
                                    const value = context.parsed?.y ?? context.value ?? 0
                                    if (label.includes('Процент')) {
                                        return `${label}: ${value}%`
                                    }
                                    return `${label}: ${value.toLocaleString('ru-RU')}`
                                },
                            },
                        },
                        scales: {
                            xAxes: [
                                {
                                    scaleLabel: {
                                        display: true,
                                        labelString: 'Выручка (Р)',
                                    },
                                    stacked: false,
                                },
                            ],
                            yAxes: [
                                {
                                    scaleLabel: {
                                        display: true,
                                        labelString: 'Премия (Р) / Процент (%)',
                                    },
                                    ticks: {
                                        beginAtZero: true,
                                        callback: function(value: any) {
                                            return value.toLocaleString('ru-RU')
                                        },
                                    },
                                    stacked: false,
                                },
                            ],
                        },
                    },
                })
            },

            createKpiChart() {
                const canvas = this.$refs.kpiChart as HTMLCanvasElement
                if (!canvas) return

                // Уничтожаем предыдущий график если есть
                if (this.kpiChartInstance) {
                    this.kpiChartInstance.destroy()
                }

                // Получаем данные из таблицы KPI
                const kpiData = this.getKpiScaleData()
                const kpiColumns = this.kpiScaleColumns

                this.kpiChartInstance = new Chart(canvas, {
                    type: 'line',
                    data: {
                        labels: kpiData.labels,
                        datasets: [
                            {
                                label: '% от номинального бонуса',
                                data: kpiData.bonuses,
                                borderColor: '#28a745',
                                backgroundColor: 'rgba(40, 167, 69, 0.1)',
                                fill: true,
                                pointBackgroundColor: '#28a745',
                                pointBorderColor: '#fff',
                                pointBorderWidth: 2,
                                pointRadius: 6,
                            },
                        ],
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        title: {
                            display: false,
                        },
                        legend: {
                            display: false,
                        },
                        tooltips: {
                            displayColors: false,
                            callbacks: {
                                title: function() {
                                    return ''
                                },
                                label: function(tooltipItem: any, data: any) {
                                    const index = tooltipItem.index
                                    const column = kpiColumns[index]
                                    if (!column) {
                                        return []
                                    }
                                    const completion = column.completion || 0
                                    const bonus = column.bonusPercentage || 0
                                    return [
                                        `Итоговый % выполнения KPI: ${completion}%`,
                                        `% от Номинального бонуса за KPI: ${bonus}%`,
                                    ]
                                },
                            },
                        },
                        scales: {
                            xAxes: [
                                {
                                    scaleLabel: {
                                        display: true,
                                        labelString: 'Выполнение KPI (%)',
                                    },
                                },
                            ],
                            yAxes: [
                                {
                                    scaleLabel: {
                                        display: true,
                                        labelString: '% от номинального бонуса',
                                    },
                                    ticks: {
                                        beginAtZero: true,
                                    },
                                },
                            ],
                        },
                    },
                })
            },

            getRevenueScaleDataForBranch(branchId: string) {
                // Получаем данные из таблицы выручки для конкретного филиала
                const scaleData = this.branchRevenueScales[branchId] || []

                // Создаем данные для столбчатого графика с тремя столбцами для каждого значения
                const labels = scaleData.map((item: any, index: number) => {
                    const revenue = item.revenue || 0
                    return `${revenue.toLocaleString('ru-RU')}Р`
                })

                return {
                    labels,
                    datasets: [
                        {
                            label: 'Выручка (Р)',
                            data: scaleData.map((item: any) => item.revenue || 0),
                            backgroundColor: 'rgba(0, 123, 255, 0.8)',
                            borderColor: '#007bff',
                            borderWidth: 1,
                        },
                        {
                            label: 'Премия (Р)',
                            data: scaleData.map((item: any) => item.bonus || 0),
                            backgroundColor: 'rgba(255, 193, 7, 0.8)',
                            borderColor: '#ffc107',
                            borderWidth: 1,
                        },
                        {
                            label: 'Процент (%)',
                            data: scaleData.map((item: any) => item.percentage || 0),
                            backgroundColor: 'rgba(40, 167, 69, 0.8)',
                            borderColor: '#28a745',
                            borderWidth: 1,
                        },
                    ],
                }
            },

            getRevenueScaleData() {
                // Получаем данные из первой таблицы выручки (для филиалов или общей)
                let scaleData = []

                if (this.plan.branchIds && this.plan.branchIds.length > 1) {
                    // Если есть филиалы, берем данные из первого филиала
                    const firstBranch = this.plan.branchIds[0]
                    scaleData = this.branchRevenueScales[firstBranch] || []
                } else {
                    // Иначе берем общие данные
                    scaleData = this.revenueScale
                }

                // Создаем данные для столбчатого графика с тремя столбцами для каждого значения
                const labels = scaleData.map((item: any, index: number) => {
                    const revenue = item.revenue || 0
                    return `${revenue.toLocaleString('ru-RU')}Р`
                })

                return {
                    labels,
                    datasets: [
                        {
                            label: 'Выручка (Р)',
                            data: scaleData.map((item: any) => item.revenue || 0),
                            backgroundColor: 'rgba(0, 123, 255, 0.8)',
                            borderColor: '#007bff',
                            borderWidth: 1,
                        },
                        {
                            label: 'Премия (Р)',
                            data: scaleData.map((item: any) => item.bonus || 0),
                            backgroundColor: 'rgba(255, 193, 7, 0.8)',
                            borderColor: '#ffc107',
                            borderWidth: 1,
                        },
                        {
                            label: 'Процент (%)',
                            data: scaleData.map((item: any) => item.percentage || 0),
                            backgroundColor: 'rgba(40, 167, 69, 0.8)',
                            borderColor: '#28a745',
                            borderWidth: 1,
                        },
                    ],
                }
            },

            getKpiScaleData() {
                return {
                    labels: this.kpiScaleColumns.map((item: any) => item.completion?.toString() || '0'),
                    bonuses: this.kpiScaleColumns.map((item: any) => item.bonusPercentage || 0),
                }
            },

            updateCharts() {
                // Предотвращаем бесконечный цикл
                if (this.isUpdatingCharts) {
                    return
                }

                this.isUpdatingCharts = true

                // Обновляем все графики при изменении данных
                this.$nextTick(() => {
                    // Создаем графики для всех филиалов
                    if (this.plan.branchIds && this.plan.branchIds.length > 1) {
                        this.plan.branchIds.forEach((branchId: string) => {
                            this.createRevenueChart(branchId)
                        })
                    }
                    this.createRevenueChartGeneral()
                    this.createKpiChart()

                    // Сбрасываем флаг после завершения обновления
                    this.$nextTick(() => {
                        this.isUpdatingCharts = false
                    })
                })
            },
        },
    })
